import React, { ComponentProps, InputHTMLAttributes, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import Icon from 'shared/components/Icon';

interface IUploadFileProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  file?: (File & Record<string, any>) | null;
  onRemoveFile?: (file: IUploadFileProps['file']) => void;
  clearInput?: boolean;
}

const commonProps: ComponentProps<typeof motion.div> = {
  variants: {
    hidden: { opacity: 0, display: 'none' },
    visible: { opacity: 1, display: 'flex' },
  },
  initial: 'hidden',
  exit: 'hidden',
};

const UploadFile: React.FC<IUploadFileProps> = ({
  children,
  file,
  onRemoveFile,
  clearInput,
  ...restProps
}) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const { t } = useTranslation();

  useEffect(() => {
    if (clearInput && inputRef.current?.value) {
      inputRef.current.value = '';
    }
  }, [clearInput]);

  return (
    <UploadFileContainer>
      <AnimatePresence>
        <motion.div
          className="upload-container"
          animate={file ? 'hidden' : 'visible'}
          {...commonProps}
        >
          <input
            ref={inputRef}
            type="file"
            id="upload-file"
            name="upload-file"
            {...restProps}
          />
          <div className="file-desc-container">
            <Icon width="16" height="16" viewBox="0 0 16 16" glyph="cloud" />
            <div className="file-desc">{children}</div>
          </div>
        </motion.div>
        <motion.div
          className="file-container"
          animate={file ? 'visible' : 'hidden'}
          {...commonProps}
        >
          <Icon
            fill="none"
            width="24"
            height="24"
            viewBox="0 0 20 20"
            glyph="attachment"
          />
          <div className="file-name">{file?.name}</div>
          <div
            className="file-remove"
            onClick={() => {
              //reset uploaded file in the input
              if (inputRef.current) {
                inputRef.current.value = '';
              }
              onRemoveFile?.(file);
            }}
          >
            {t('contacts.menu.remove.text')}
          </div>
        </motion.div>
      </AnimatePresence>
    </UploadFileContainer>
  );
};

export default UploadFile;

const UploadFileContainer = styled.div`
  display: flex;
  align-items: stretch;
  flex-wrap: nowrap;

  .upload-container {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    cursor: pointer;
    padding: 24px;
    background: #fcfcfc;
    border: 1px dashed #dddddd;
    box-shadow: 0px 2px 4px rgba(var(--color-elephant-900), 0.06);
    border-radius: 8px;
    position: relative;

    input[type='file'] {
      position: absolute;
      left: 0;
      top: 0;
      opacity: 0;
      width: 100%;
      height: 100%;
      cursor: pointer;
    }
  }

  .file-desc-container {
    display: flex;
    align-items: center;

    .file-desc {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding-left: 24px;
    }
  }

  .file-container {
    width: 100%;
    border: 1px solid var(--color-smoke-100);
    border-radius: 8px;
    background: #f3f5f8;
    display: flex;
    align-items: center;
    gap: 2px;
    padding: 12px 14px;

    & svg {
      transform: rotate(45deg);
      & > rect {
        display: none;
      }
      & > path {
        stroke: var(--color-elephant-900);
      }
    }
    .file-name {
      flex: 1;
      font-weight: 600;
      font-size: 14px;
      color: var(--color-elephant-900);
    }
    .file-remove {
      font-size: 14px;
      font-weight: 500;
      color: var(--color-azure-800);

      cursor: pointer;
    }
  }
`;
